/**
 * FHIR extension URLs, CodeSystems, and value-set options for the Client User Profile.
 *
 * PRD sources:
 *   docs/prd/users/PRD_CLIENTS_FHIR_MAPPING.md
 *   docs/prd/users/PRD_NON_CLIENT_RELATED_PERSONS_FHIR_MAPPING.md
 *   docs/prd/users/PRD_RBAC_FHIR_MAPPING.md
 *
 * Patient.name use='official'  → legal name
 * Patient.name use='nickname'  → preferred/display name
 * Patient.birthDate            → date of birth
 * Patient.telecom system=phone → phone
 * Patient.telecom system=email → email
 * Patient.address              → home address
 * Patient.communication        → languages spoken
 * Patient.contact              → emergency / other contacts
 * Patient.link → RelatedPerson → family/couples contacts
 */

import {
  codedOptionDisplays,
  ETHNICITY_OPTIONS,
  FAITH_OPTIONS,
  GENDER_OPTIONS,
  SEXUALITY_OPTIONS,
} from '@unify/fhir-model/provider-profile-datasets';

// ---------------------------------------------------------------------------
// Extension URLs (Unify-specific)
// ---------------------------------------------------------------------------

export const PATIENT_EXT = {
  GENDER_IDENTITY: 'https://unify.health/fhir/gender-identity',
  LGBTQIA_IDENTITIES: 'https://unify.health/fhir/lgbtqia-identities',
  RELIGION_SPIRITUALITY: 'https://unify.health/fhir/religion-spirituality',
  RACE_ETHNICITY: 'https://unify.health/fhir/race-ethnicity',
  PRONOUNS: 'https://unify.health/fhir/pronouns',
  PORTAL_SUBJECT_PATIENT: 'https://unify.health/fhir/portal-subject-patient',
  PORTAL_RELATED_PATIENT: 'https://unify.health/fhir/portal-related-patient',
} as const;

export const EMERGENCY_CONTACT_RELATIONSHIP_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v2-0131';
export const EMERGENCY_CONTACT_CODE = 'C';

export const V3_ROLE_CODE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-RoleCode';

// ---------------------------------------------------------------------------
// Profile form option lists
// ---------------------------------------------------------------------------

export const GENDER_IDENTITY_OPTIONS = [...codedOptionDisplays(GENDER_OPTIONS)] as const;

export const LGBTQIA_OPTIONS = [...codedOptionDisplays(SEXUALITY_OPTIONS), 'Queer'] as const;

export const RELIGION_OPTIONS = [...codedOptionDisplays(FAITH_OPTIONS)] as const;

export const RACE_ETHNICITY_OPTIONS = [...codedOptionDisplays(ETHNICITY_OPTIONS)] as const;

export const LANGUAGES_OPTIONS = [
  'Arabic',
  'Armenian',
  'Cantonese',
  'English',
  'Farsi',
  'French',
  'German',
  'Hebrew',
  'Hindi',
  'Korean',
  'Mandarin',
  'Russian',
  'Spanish',
  'Tagalog (incl. Filipino)',
  'Vietnamese',
] as const;

// ---------------------------------------------------------------------------
// Contact relationship types used in Patient.contact / the profile form
// ---------------------------------------------------------------------------

export const CONTACT_RELATIONSHIP_V3_CODES: Record<string, string> = {
  Child: 'CHILD',
  Dependent: 'CHILD',
  'Family Member': 'FAMMEMB',
  'Legal Guardian': 'GUARD',
  /** v2-0131 Consulting Provider — not v3 RoleCode (PRN is Parent). */
  Physician: 'CP',
  Parent: 'PRN',
  Partner: 'DOMPART',
  Spouse: 'SPS',
  Patient: 'ONESELF',
  Other: 'O',
} as const;

export const V3_CODE_TO_RELATIONSHIP_LABEL: Record<string, string> = {
  CHILD: 'Child',
  FAMMEMB: 'Family Member',
  GUARD: 'Legal Guardian',
  PRN: 'Parent',
  FTH: 'Father',
  MTH: 'Mother',
  CP: 'Physician',
  SPS: 'Spouse',
  DOMPART: 'Partner',
  SIB: 'Sibling',
  FRND: 'Friend',
  ONESELF: 'Patient',
  O: 'Other',
} as const;

export function relationshipCodingForType(
  type: string,
): { system: string; code: string; display: string } | undefined {
  const code = CONTACT_RELATIONSHIP_V3_CODES[type];
  if (!code) return undefined;
  const system = type === 'Physician' ? EMERGENCY_CONTACT_RELATIONSHIP_SYSTEM : V3_ROLE_CODE_SYSTEM;
  return { system, code, display: type };
}
